import styled from '@emotion/styled';
import { css, keyframes } from '@emotion/react';
import { tokens } from '../../../design-system/tokens';
import type { SelectAppearance } from './Select.types';

export const SelectContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${tokens.spacing.xs};

  &:disabled {
    opacity: 0.5;
  }
`;

export const SelectButton = styled.div<{
  $hasValue: boolean;
  $isMulti?: boolean;
  $isDisabled?: boolean;
  $hasSearchTerm?: boolean;
  $appearance: SelectAppearance;
}>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 40px;
  padding: 0 ${tokens.spacing.md};
  box-sizing: border-box;

  background-color: ${({ $appearance }) =>
    $appearance === 'dark'
      ? tokens.colors.blue[500]
      : tokens.colors.inputBackground};
  border: 1px solid ${({ $appearance }) =>
    $appearance === 'dark'
      ? tokens.colors.basics[700]
      : tokens.colors.inputBackground};
  border-radius: ${tokens.borderRadius.sm};

  color: ${({ $appearance, $hasValue, $isMulti, $hasSearchTerm }) =>
    $appearance === 'dark'
      ? tokens.colors.basics[0]
      : ($hasValue && !$isMulti) || $hasSearchTerm
        ? tokens.colors.basics[1400]
        : tokens.colors.placeholder};

  font-size: ${tokens.typography.fontSize.sm};
  font-family: ${tokens.typography.fontFamily.primary};
  font-weight: ${tokens.typography.fontWeight.semibold};
  line-height: ${tokens.typography.lineHeight.sm};
  cursor: pointer;
  transition: ${tokens.transitions.fast};
  text-align: left;
  max-width: 100%;

  ${({ $appearance, $isDisabled }) =>
    !$isDisabled &&
    ($appearance === 'dark'
      ? css`
          &:hover {
            border: 1px solid ${tokens.colors.basics[0]};
          }

          &:focus-within {
            border: 1px solid ${tokens.colors.basics[700]};
            box-shadow: none;
            outline: none;
          }
        `
      : css`
          &:hover {
            border: 3px solid ${tokens.colors.basics[500]};
          }

          &:focus-within {
            border-color: ${tokens.colors.secondary[500]};
            box-shadow: 0 0 0 2px ${tokens.colors.blue[200]};
            outline: none;
          }
        `)}

  ${({ $isDisabled }) =>
    $isDisabled &&
    `
    border: none;
    background-color: ${tokens.colors.basics[300]};
    color: ${tokens.colors.basics[1100]};
    cursor: not-allowed;
    opacity: 0.2;
    pointer-events: none;
  `}
`;

export const SelectTopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-width: 0;

  > span {
    display: block;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const SelectControls = styled.div`
  display: flex;
  align-items: center;
  gap: ${tokens.spacing.xs};
  flex: 0 0 auto;
`;

export const ClearButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px;
  display: flex;
  align-items: center;
  color: ${tokens.colors.basics[1200]};
  transition: ${tokens.transitions.fast};

  &:hover {
    color: ${tokens.colors.basics[1100]};
  }

  &:focus {
    outline: 2px solid ${tokens.colors.blue[200]};
    outline-offset: 1px;
    border-radius: 2px;
  }
`;

export const SelectArrow = styled.div<{
  $isOpen: boolean;
  $appearance: SelectAppearance;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ $appearance }) =>
    $appearance === 'dark'
      ? tokens.colors.basics[0]
      : tokens.colors.blue[800]};
  transition: ${tokens.transitions.fast};
  transform: ${({ $isOpen }) => ($isOpen ? 'rotate(270deg)' : 'rotate(90deg)')};
`;

/* Dropdown */
export const SelectDropdown = styled.div<{
  $appearance: SelectAppearance;
}>`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: ${tokens.zIndex.dropdown};
  background: ${({ $appearance }) =>
    $appearance === 'dark'
      ? `linear-gradient(
          180deg,
          rgba(33, 77, 97, 0.99) 0%,
          rgba(31, 112, 143, 0.99) 100%
        )`
      : tokens.colors.inputBackground};
  border: 1px solid ${({ $appearance }) =>
    $appearance === 'dark'
      ? tokens.colors.basics[800]
      : tokens.colors.inputBackground};
  border-radius: ${tokens.borderRadius.sm};
  box-shadow: ${tokens.shadows.xl};
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: ${tokens.spacing.xs};
  }
  &::-webkit-scrollbar-track {
    background: ${tokens.colors.basics[400]};
  }
  &::-webkit-scrollbar-thumb {
    background: ${tokens.colors.basics[1000]};
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: ${tokens.colors.basics[1100]};
  }
`;

export const SearchInputContainer = styled.div`
  padding: ${tokens.spacing.sm};
  border-bottom: 1px solid ${tokens.colors.basics[500]};
`;

export const SearchInput = styled.input`
  width: 100%;
  padding: ${tokens.spacing.xs} ${tokens.spacing.sm};
  border: 1px solid ${tokens.colors.basics[600]};
  border-radius: ${tokens.borderRadius.sm};
  font-size: ${tokens.typography.fontSize.sm};
  font-family: ${tokens.typography.fontFamily.primary};
  background-color: ${tokens.colors.inputBackground};

  &:focus {
    outline: none;
    border-color: ${tokens.colors.blue[500]};
    box-shadow: 0 0 0 2px ${tokens.colors.blue[200]};
  }

  &::placeholder {
    color: ${tokens.colors.placeholder};
  }
`;

// Inline search input rendered inside the control when dropdown is open
export const InlineSearchInput = styled.input<{
  $appearance: SelectAppearance;
}>`
  width: 100%;
  min-width: 0;
  padding: 0; /* Use control padding */
  border: none;
  background: transparent;
  color: ${({ $appearance }) =>
    $appearance === 'dark'
      ? tokens.colors.basics[0]
      : tokens.colors.blue[900]};
  font-size: ${tokens.typography.fontSize.sm};
  font-family: ${tokens.typography.fontFamily.primary};
  font-weight: ${tokens.typography.fontWeight.semibold};
  line-height: ${tokens.typography.lineHeight.sm};

  &:focus {
    outline: none;
  }

  &::placeholder {
    color: ${({ $appearance }) =>
      $appearance === 'dark'
        ? tokens.colors.basics[700]
        : tokens.colors.placeholder};
  }
`;

export const SelectAllContainer = styled.div`
  padding: ${tokens.spacing.sm};
  border-bottom: 1px solid ${tokens.colors.basics[500]};
`;

export const SelectAllButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: ${tokens.typography.fontSize.sm};
  font-family: ${tokens.typography.fontFamily.primary};
  font-weight: ${tokens.typography.fontWeight.medium};
  color: ${tokens.colors.blue[600]};
  transition: ${tokens.transitions.fast};

  &:hover {
    color: ${tokens.colors.blue[800]};
  }

  &:focus {
    outline: 2px solid ${tokens.colors.blue[200]};
    outline-offset: 1px;
    border-radius: 2px;
  }
`;

export const SelectOption = styled.div<{
  $isSelected: boolean;
  $isDisabled: boolean;
  $isMulti?: boolean;
  $appearance: SelectAppearance;
}>`
  display: flex;
  min-width: 0;
  box-sizing: border-box;
  align-items: center;
  padding: ${tokens.spacing.sm}
    ${({ $isMulti }) => ($isMulti ? tokens.spacing.md : "44px")}
    ${tokens.spacing.sm} ${tokens.spacing.md};
  cursor: ${({ $isDisabled }) => ($isDisabled ? 'not-allowed' : 'pointer')};
  transition: ${tokens.transitions.fast};
  font-size: ${tokens.typography.fontSize.sm};
  font-family: ${tokens.typography.fontFamily.primary};
  font-weight: ${tokens.typography.fontWeight.medium};
  position: relative;

  color: ${({ $appearance, $isDisabled }) =>
    $appearance === 'dark'
      ? tokens.colors.basics[0]
      : $isDisabled
        ? tokens.colors.basics[1000]
        : tokens.colors.basics[1400]};

  > span {
    min-width: 0;
    overflow-wrap: anywhere;
  }

  ${({ $isDisabled }) =>
    $isDisabled &&
    `
    opacity: 0.5;
    pointer-events: none;
  `}

  ${({ $appearance, $isMulti }) =>
    $appearance !== 'dark' && $isMulti &&
    `
    background-color: 'transparent';

    &:hover:not([aria-disabled="true"]) {
      background-color: ${tokens.colors.basics[800]};
    }
  `}

  ${({ $appearance, $isMulti }) =>
    $appearance !== 'dark' && !$isMulti &&
    `
    &:hover:not([aria-disabled="true"]) {
      background-color: ${tokens.colors.basics[200]};
    }
  `}

  ${({ $appearance }) =>
    $appearance === 'dark' &&
    `
    &:hover:not([aria-disabled="true"]) {
      background-color: ${tokens.colors.basics[800]};
    }
  `}
`;

export const OptionCheckbox = styled.input`
  margin-right: ${tokens.spacing.sm};
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  border: 2px solid ${tokens.colors.blue[500]};
  border-radius: 4px;
  background: ${tokens.colors.inputBackground};
  display: inline-grid;
  place-content: center;
  position: relative;
  accent-color: ${tokens.colors.secondary[500]};

  &::after {
    content: '';
    position: relative;
    top: -2px;
    width: 5px;
    height: 9px;
    border-right: 2px solid ${tokens.colors.basics[0]};
    border-bottom: 2px solid ${tokens.colors.basics[0]};
    transform: rotate(45deg);
    opacity: 0;
  }

  &:checked {
    background: ${tokens.colors.gradient.primary};
    border-color: ${tokens.colors.basics[0]};
  }
  &:checked::after {
    opacity: 1;
  }

  &:focus {
    outline: 2px solid ${tokens.colors.blue[200]};
    outline-offset: 1px;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

export const IconWrapper = styled.div<{
  $appearance: SelectAppearance;
}>`
  position: absolute;
  right: ${tokens.spacing.md};
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  color: ${({ $appearance }) =>
    $appearance === 'dark'
      ? tokens.colors.basics[0]
      : tokens.colors.blue[900]};
`;

/* ──────────────────────────────────────────────────────────
   TAGS: barra de altura fija + carrusel horizontal
   ────────────────────────────────────────────────────────── */

export const TagsBar = styled.div`
  position: relative;
  width: 100%;
  height: 32px; /* altura fija: no crece */
  margin-top: ${tokens.spacing.xs};

`;

export const MultiSelectTags = styled.div<{ showLeft: boolean; showRight: boolean; }>`
  position: absolute;
  inset: ${({ showLeft, showRight }) => {
    const left = showLeft ? "22px" : "0";
    const right = showRight ? "22px" : "0";
    return `0 ${right} 15px ${left}`;
  }};

  display: flex;
  align-items: center;
  gap: ${tokens.spacing.xs};
  overflow-x: auto;
  overflow-y: hidden;
  white-space: nowrap;
  height: 100%;

  /* No permitir que el contenido expanda */
  & > * {
    flex-shrink: 0;
    white-space: nowrap;
  }

  /* Scrollbar horizontal (discreto) */
  &::-webkit-scrollbar {
    height: ${tokens.spacing.xs};
  }
  &::-webkit-scrollbar-track {
    background: ${tokens.colors.basics[400]};
    border-radius: 2px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${tokens.colors.basics[1000]};
    border-radius: 2px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: ${tokens.colors.basics[1100]};
  }

  -ms-overflow-style: none;      /* IE y Edge antiguos */
  scrollbar-width: none;         /* Firefox */
  &::-webkit-scrollbar {         /* Chrome, Safari, Edge Chromium */
    width: 0;
    height: 0;
    display: none;
    background: transparent;
  }
`;

export const ScrollButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%) rotate(0deg);
  border-radius: 50%;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  /* 🔧 quita el highlight de click en iOS/Safari/Chrome */
  -webkit-tap-highlight-color: transparent;

  /* 🔧 quita el borde interno en Firefox */
  &::-moz-focus-inner { border: 0; }

  /* ❗️el bug estaba acá: faltaba la coma */
  &:focus,
  &:focus-visible {
    outline: none;
    box-shadow: none;  /* por si algún UA usa shadow */
  }

  &[data-side="left"] { left: 0; }

  &[data-side="right"] {
    right: 0;
    svg { transform: rotate(180deg); }
  }

  &:hover { filter: brightness(1.05); }
  &:active { transform: translateY(-50%) scale(0.98); }
`;

/* Otros estilos existentes */

export const EmptyState = styled.div<{
  $appearance: SelectAppearance;
}>`
  padding: ${tokens.spacing.lg};
  text-align: center;
  color: ${({ $appearance }) =>
    $appearance === 'dark'
      ? tokens.colors.basics[0]
      : tokens.colors.basics[1100]};
  font-size: ${tokens.typography.fontSize.sm};
  font-family: ${tokens.typography.fontFamily.primary};
`;

export const SwitchContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${tokens.spacing.xs};

  span {
    font-size: ${tokens.typography.fontSize.sm};
    font-weight: ${tokens.typography.fontWeight.light};
    color: ${tokens.colors.basics[0]};
    font-family: ${tokens.typography.fontFamily.primary};
  }
`;

export const SwitchAction = styled.div`
  display: flex;
  align-items: center;
  gap: ${tokens.spacing.sm};
`;

export const CustomLabel = styled.label`
  display: block;
  margin-bottom: ${tokens.spacing.xs};
  font-size: ${tokens.typography.fontSize.sm};
  font-weight: ${tokens.typography.fontWeight.light};
  color: ${tokens.colors.basics[0]};
  font-family: ${tokens.typography.fontFamily.primary};
`;

export const TagRemoveButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  color: ${tokens.colors.basics[0]};
  margin-left: ${tokens.spacing.xs};
  transition: ${tokens.transitions.fast};

  &:hover {
    opacity: 0.8;
  }

  &:focus {
    outline: 2px solid ${tokens.colors.basics[0]};
    outline-offset: 1px;
    border-radius: 2px;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`
// Loading row for options
const rotate = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

export const OptionsLoading = styled.div<{
  $appearance: SelectAppearance;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${tokens.spacing.sm};
  padding: ${tokens.spacing.lg};
  color: ${({ $appearance }) =>
    $appearance === 'dark'
      ? tokens.colors.basics[0]
      : tokens.colors.basics[1100]};
  font-size: ${tokens.typography.fontSize.sm};
  font-family: ${tokens.typography.fontFamily.primary};
`;

export const SmallSpinner = styled.span`
  width: 16px;
  height: 16px;
  border: 2px solid ${tokens.colors.blue[200]};
  border-top-color: ${tokens.colors.blue[600]};
  border-radius: 50%;
  display: inline-block;
  animation: ${rotate} 0.9s linear infinite;
`;


export const Tag = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: ${tokens.colors.blue[700]};
  border-radius: ${tokens.borderRadius.sm};
  padding: ${tokens.spacing.xs} ${tokens.spacing.sm};
  margin-right: ${tokens.spacing.xs};
  color: ${tokens.colors.basics[0]};
  font-size: ${tokens.typography.fontSize.xs};
  font-family: ${tokens.typography.fontFamily.primary};
  font-weight: ${tokens.typography.fontWeight.medium};
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  border-radius: ${tokens.borderRadius.full};
`
